import React, { useEffect, useState, useRef } from 'react';
import { ArrowUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BrandMark } from '../BrandMark';
export interface FooterLink {
  href: string;
  label: string;
}
interface AnimatedFooterProps {
  leftLinks: FooterLink[];
  rightLinks: FooterLink[];
  copyrightText: string;
  barCount?: number;
}
export function AnimatedFooter({
  leftLinks,
  rightLinks,
  copyrightText,
  barCount = 23
}: AnimatedFooterProps) {
  const waveRefs = useRef<Array<HTMLDivElement | null>>([]);
  const footerRef = useRef<HTMLElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      threshold: 0.15
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!isVisible || prefersReducedMotion) return;
    let time = 0;
    const animateWave = () => {
      let offset = 0;
      waveRefs.current.forEach((element, index) => {
        if (!element) return;
        offset += Math.max(0, 13 * Math.sin((time + index * 0.8) * 0.32));
        element.style.transform = `translateY(${index + offset}px)`;
      });
      time += 0.065;
      animationFrameRef.current = requestAnimationFrame(animateWave);
    };
    animateWave();
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isVisible, prefersReducedMotion]);
  return <footer ref={footerRef} className="relative flex w-full select-none flex-col justify-between overflow-hidden border-t border-white/10 bg-black text-white" data-id="element-140" data-mp="3g62f6snp82d6">
      <div className="container relative z-10 mx-auto grid w-full gap-10 px-6 pb-12 pt-12 md:grid-cols-[1fr_auto] md:gap-16 md:px-12" data-id="element-141" data-mp="369h9s67rop8t">
        <div data-id="element-142" data-mp="2wk61umwm9qmi">
          <p className="flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.22em] text-white" data-id="element-143" data-mp="2wje87hmky2yv">
            <BrandMark size={16} data-id="element-144" data-mp="31aw965ols3ju" />
            GROWTHLY
          </p>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-400" data-id="element-145" data-mp="2nj7rznzhyow0">
            Diseño y presencia digital para negocios que quieren verse tan bien
            como trabajan.
          </p>
          <nav className="mt-7" aria-label="Enlaces legales" data-id="element-146" data-mp="2dlblj9hi8i48">
            <ul className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-147" data-mp="2dltp6r6hxym6">
              {leftLinks.map(link => <li key={link.href} data-id="element-148" data-mp="2im92jiqs3645">
                  <Link to={link.href} className="text-[13px] text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-149" data-mp="1yy38s48js4q5">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
          <p className="mt-7 text-xs leading-relaxed text-neutral-400" data-id="element-150" data-mp="23zit1u2wi77h">
            {copyrightText}
          </p>
        </div>
        <div data-id="element-151" data-mp="2dvvi2eni3wvw">
          <p className="meta-label" data-id="element-152" data-mp="2dw58228jq41l">Navegación</p>
          <nav className="mt-5" aria-label="Navegación del pie de página" data-id="element-153" data-mp="2dtj40ckv66o4">
            <ul className="grid grid-cols-2 gap-x-10 gap-y-3 sm:grid-cols-3 md:grid-cols-2" data-id="element-154" data-mp="2rlpaf6y7szad">
              {rightLinks.map(link => <li key={link.href} data-id="element-155" data-mp="2mq9yikjrpdgl">
                  <Link to={link.href} className="text-[13px] text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-156" data-mp="31keth31ukps7">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
          <button type="button" onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })} className="mt-7 inline-flex items-center gap-2 text-xs text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-157" data-mp="13p9bresy0klh">
            Volver arriba <ArrowUpIcon size={14} aria-hidden="true" data-id="element-158" data-mp="kyrohpqvqh7i" />
          </button>
        </div>
      </div>

      <div className="pointer-events-none relative h-[120px] overflow-hidden sm:h-[150px]" aria-hidden="true" data-id="element-159" data-mp="pxuouwvwzz95">
        <div className="absolute inset-x-0 bottom-0" data-id="element-160" data-mp="1wcvfcn267p8c">
          {Array.from({
          length: barCount
        }).map((_, index) => <div key={index} ref={element => {
          waveRefs.current[index] = element;
        }} className="bg-white motion-reduce:translate-y-0" style={{
          height: `${index + 1}px`,
          marginTop: '-2px',
          willChange: 'transform'
        }} data-id="element-161" data-mp="21f46rezeaj8c" data-mpi={index} />)}
        </div>
      </div>
    </footer>;
}